'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

const keyFor = (path: string) => `__scroll:${path}`;

export function useScrollManager({ manual = true } = {}) {
  const pathname = usePathname();
  const search = useSearchParams();
  const path = `${pathname}${search?.toString() ? `?${search.toString()}` : ''}`;
  const prevPathRef = useRef<string>(path);

  useEffect(() => {
    // Turn off browser auto-restoration so we control it.
    if (manual && 'scrollRestoration' in history) {
      const prev = (history as any).scrollRestoration;
      (history as any).scrollRestoration = 'manual';
      return () => { (history as any).scrollRestoration = prev; };
    }
  }, [manual]);

  // Save scroll for the page we’re leaving
  useEffect(() => {
    const save = () => {
      const prevPath = prevPathRef.current;
      if (!prevPath) return;
      try {
        sessionStorage.setItem(keyFor(prevPath), String(window.scrollY || document.documentElement.scrollTop || 0));
      } catch {}
    };

    // Save on visibility change/unload (covers Cmd/Ctrl+L, reload, etc.)
    const handleHide = () => { save(); };
    document.addEventListener('visibilitychange', handleHide);
    window.addEventListener('beforeunload', save);

    return () => {
      document.removeEventListener('visibilitychange', handleHide);
      window.removeEventListener('beforeunload', save);
    };
  }, []);

  // On route change, save old scroll and restore new scroll if we have it
  useEffect(() => {
    const prevPath = prevPathRef.current;
    if (prevPath !== path) {
      try {
        sessionStorage.setItem(keyFor(prevPath), String(window.scrollY || 0));
      } catch {}
      const stored = typeof window !== 'undefined' ? sessionStorage.getItem(keyFor(path)) : null;
      if (stored) {
        // instant to avoid animation
        window.scrollTo({ top: Number(stored) || 0, behavior: 'instant' as ScrollBehavior });
      } else {
        // default behavior on first visit
        window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
      }
      prevPathRef.current = path;
    }
  }, [path]);
}